import { 
  doc, 
  getDoc, 
  setDoc, 
  deleteDoc, 
  collection, 
  query, 
  orderBy, 
  getDocs, 
  serverTimestamp,
  updateDoc
} from 'firebase/firestore';
import { db, auth } from '../../../../firebase/firebase';

/**
 * JournalStorageService - Manages journal entries with Firestore storage and localStorage fallback
 * 
 * Features:
 * - Firestore primary storage with localStorage fallback
 * - Data validation and conversation hash generation
 * - Automatic image assignment and tag extraction
 * - Import/export capabilities
 * - Migration and cleanup utilities
 */
class JournalStorageService {
constructor(firestoreService = null) {
  this.firestoreService = firestoreService;
  this.localStorageKey = 'voice_journals';
  this.db = db;  // ← ADD THIS LINE
  this.userId = null;  // ← ADD THIS LINE
}
  // ================================
  // CORE SETUP & UTILITIES
  // ================================

  /**
   * Check if service is ready to use Firestore
   */
  isReady() {
    return auth.currentUser && (this.firestoreService?.isInitialized || true);
  }

  /**
   * Get journals collection reference for current user
   */
  getJournalsCollectionRef() {
    if (!auth.currentUser) {
      throw new Error('User not authenticated');
    }
    return collection(db, 'users', auth.currentUser.uid, 'journals');
  }

  /**
   * Generate conversation hash for validation
   */
  generateConversationHash(conversation) {
    if (!Array.isArray(conversation) || conversation.length === 0) {
      return 'empty_conversation';
    }
    
    const content = conversation
      .filter(msg => msg.type !== 'system')
      .map(msg => `${msg.type}:${msg.content}`)
      .join('');
    
    // Simple hash function
    let hash = 0;
    for (let i = 0; i < content.length; i++) {
      const char = content.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash).toString(36);
  }

  /**
   * Generate unique image URL for each journal
   */
  generateUniqueImage(journalId) {
    // Use journal ID to create a unique seed
    const seed = parseInt(journalId.replace(/\D/g, '')) || Math.abs(journalId.split('').reduce((a, b) => a + b.charCodeAt(0), 0));
    
    // Array of local image paths
    const imageCategories = [
      '/image/session-img13.png',
      '/image/session-img51.png',
      '/image/session-img32.png',
      '/image/session-img8.png',
      '/image/session-img33.png',
      '/image/session-img10.png',
      '/image/session-img14.png',
      '/image/session-img11.png',
      '/image/session-img20.png',
      '/image/session-img21.png',
      '/image/session-img22.png',
      '/image/session-img49.png',
      '/image/session-img24.png',
       '/image/session-img25.png',
       '/image/session-img31.png',
           '/image/session-img42.png',
               '/image/session-img44.png',
            '/image/session-img36.png',
            '/image/session-img49.png',
            '/image/session-img50.png',
    ];
    
    // Select image based on seed to ensure consistent selection for each journal
    const imageIndex = seed % imageCategories.length;
    const selectedImage = imageCategories[imageIndex];
    
    console.log(`Generated unique image for journal ${journalId}:`, selectedImage);
    return selectedImage;
  }

  // ================================
  // CRUD OPERATIONS
  // ================================

async initialize(userId) {
  if (!userId) {
    console.error('❌ Cannot initialize without userId');
    return;
  }
  this.userId = userId;
  console.log('📚 Journal storage initialized for user:', userId);
}
/**
 * Get all saved journals from Firestore with localStorage fallback
 */
async getAllJournals() {
  try {
    if (!this.isReady()) {
      const localJournals = this.getLocalStorageJournals();
      return Array.isArray(localJournals) ? localJournals : [];
    }

    const journalsRef = this.getJournalsCollectionRef();
    const q = query(journalsRef, orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);

    const journals = [];
    querySnapshot.forEach((doc) => {
      const journalData = doc.data();
      
      // ✅ FIX: Convert selectedImages back to objects
      let selectedImages = [];
      if (journalData.selectedImages && Array.isArray(journalData.selectedImages)) {
        selectedImages = journalData.selectedImages.map((img, idx) => {
          if (typeof img === 'string') {
            return {
              data: img,
              id: `img_${idx}_${doc.id}`,
              width: 1920,
              height: 1080,
              aspectRatio: 16/9
            };
          }
          return img;
        });
      }
      
      journals.push({
        id: doc.id,
        ...journalData,
        selectedImages: selectedImages,
        createdAt: journalData.createdAt?.toDate?.()?.toISOString() || journalData.createdAt,
        sessionStartTime: journalData.sessionStartTime?.toDate?.()?.toISOString() || journalData.sessionStartTime,
        sessionEndTime: journalData.sessionEndTime?.toDate?.()?.toISOString() || journalData.sessionEndTime
      });
    });

    console.log(`📚 Loaded ${journals.length} journals from Firestore`);
    return journals;
    
  } catch (error) {
    console.error('Error loading journals from Firestore:', error);
    const fallbackJournals = this.getLocalStorageJournals();
    return Array.isArray(fallbackJournals) ? fallbackJournals : [];
  }
}

  /**
   * Get a specific journal by ID
   */
  async getJournalById(journalId) {
    try {
      if (this.isReady()) {
        const journalRef = doc(this.getJournalsCollectionRef(), journalId);
        const journalSnap = await getDoc(journalRef);
        
        if (journalSnap.exists()) {
          const journalData = journalSnap.data();
          return {
            id: journalSnap.id,
            ...journalData,
            createdAt: journalData.createdAt?.toDate?.()?.toISOString() || journalData.createdAt,
            sessionStartTime: journalData.sessionStartTime?.toDate?.()?.toISOString() || journalData.sessionStartTime,
            sessionEndTime: journalData.sessionEndTime?.toDate?.()?.toISOString() || journalData.sessionEndTime
          };
        }
        return null;
      } else {
        // Fallback to localStorage
        const journals = this.getLocalStorageJournals();
        return journals.find(journal => journal.id === journalId);
      }
    } catch (error) {
      console.error('Error getting journal by ID:', error);
      
      // Fallback to localStorage
      const journals = this.getLocalStorageJournals();
      return journals.find(journal => journal.id === journalId);
    }
  }

  /**
   * Save a new journal entry to Firestore with proper data validation
   */
async saveJournal(journalData) {
  try {
    if (!journalData.conversation || !Array.isArray(journalData.conversation)) {
      console.error('❌ Invalid conversation data:', journalData.conversation);
      throw new Error('Invalid conversation data provided');
    }

    const journalId = Date.now().toString();
    const currentTimestamp = new Date().toISOString();
    
    const freshConversation = Array.isArray(journalData.conversation) 
      ? journalData.conversation.map(msg => ({
          ...msg,
          timestamp: msg.timestamp || currentTimestamp
        }))
      : [];

    const generatedTitle = journalData.title || this.generateTitle(journalData);
    
    // ✅ FIX: Process selectedImages correctly
    let selectedImages = [];
    if (journalData.selectedImages && Array.isArray(journalData.selectedImages)) {
      selectedImages = journalData.selectedImages.map((img, idx) => {
        if (typeof img === 'string') {
          return img; // Already a base64 string
        }
        if (img.data) {
          return img.data; // Extract data from object
        }
        return img;
      });
    }
    
    // ✅ Use first image as background, or generate one
    const background = selectedImages[0] || this.generateUniqueImage(journalId);
    
    const newJournal = {
      id: journalId,
      title: generatedTitle,
      header: generatedTitle,
      summary: journalData.summary || '',
      insights: journalData.insights || '',
      conversation: freshConversation,
      sessionStartTime: journalData.sessionStartTime || currentTimestamp,
      sessionEndTime: currentTimestamp,
      messageCount: freshConversation.length,
      createdAt: currentTimestamp,
      tags: this.extractTags(journalData),
      imageUrl: background,
      background: background,
      selectedImages: selectedImages, // ✅ Save all images as simple string array
      question: journalData.question || '',
      userId: this.firestoreService?.getPreptuneUserId() || 'anonymous',
      mood: journalData.mood || 'thoughtful',
      writingStyle: journalData.writingStyle || '',
      journalPurpose: journalData.journalPurpose || '',
      dataValidation: {
        conversationHash: this.generateConversationHash(freshConversation),
        savedAt: currentTimestamp,
        messageCount: freshConversation.length,
        hasUserMessages: freshConversation.some(msg => msg.type === 'user'),
        hasAssistantMessages: freshConversation.some(msg => msg.type === 'assistant' || msg.type === 'bot')
      }
    };

    console.log('💾 Saving journal with images:', {
      id: newJournal.id,
      title: newJournal.title,
      imageCount: selectedImages.length,
      images: selectedImages.length > 0 ? '✅' : '❌'
    });

    if (this.isReady()) {
      const journalRef = doc(this.getJournalsCollectionRef(), journalId);
      
      const firestoreJournal = {
        ...newJournal,
        createdAt: serverTimestamp(),
        sessionStartTime: journalData.sessionStartTime ? new Date(journalData.sessionStartTime) : serverTimestamp(),
        sessionEndTime: serverTimestamp()
      };

      await setDoc(journalRef, firestoreJournal);
      console.log('✅ Journal saved to Firestore with', selectedImages.length, 'images');

      return {
        ...newJournal,
        createdAt: currentTimestamp,
        sessionEndTime: currentTimestamp
      };
    } else {
      const journals = this.getLocalStorageJournals();
      journals.unshift(newJournal);
      localStorage.setItem(this.localStorageKey, JSON.stringify(journals));
      console.log('💾 Journal saved to localStorage with', selectedImages.length, 'images');
      return newJournal;
    }
  } catch (error) {
    console.error('❌ Error saving journal:', error);
    return this.saveJournalToLocalStorage(journalData);
  }
}
async updateJournal(entryId, updatedData) {
  if (!this.userId) {
    console.error('❌ updateJournal called without userId');
    throw new Error('User not authenticated');
  }

  try {
    const docRef = doc(this.db, 'users', this.userId, 'journals', entryId);
    
    // ✅ FIX: Save all images in both fields
    const updatePayload = {
      title: updatedData.title || '',
      header: updatedData.title || '',
      summary: updatedData.content || '',
      content: updatedData.content || '',
      insights: updatedData.insights || `Mood: ${updatedData.mood || 'neutral'}`,
      mood: updatedData.mood || 'neutral',
      tags: updatedData.tags || [],
      lastModified: serverTimestamp(),
      
      // ✅ CRITICAL: Save images in BOTH fields
      images: updatedData.images && Array.isArray(updatedData.images) ? updatedData.images : [],
      selectedImages: updatedData.selectedImages && Array.isArray(updatedData.selectedImages) ? updatedData.selectedImages : [],
      background: updatedData.background || (updatedData.images?.[0]) || '',
      imageUrl: updatedData.imageUrl || (updatedData.images?.[0]) || '',
    };

    console.log('📸 Saving images array:', updatePayload.images.length, 'images');
    console.log('📸 Saving selectedImages array:', updatePayload.selectedImages.length, 'images');

    await updateDoc(docRef, updatePayload);
    
    console.log('✅ Journal updated successfully:', entryId);
    return { id: entryId, ...updatePayload };
    
  } catch (error) {
    console.error('❌ Error updating journal:', error);
    throw error;
  }
}


  /**
   * Delete a journal by ID
   */
  async deleteJournal(journalId) {
    try {
      if (this.isReady()) {
        const journalRef = doc(this.getJournalsCollectionRef(), journalId);
        await deleteDoc(journalRef);
        console.log('🗑️ Journal deleted from Firestore:', journalId);
        return true;
      } else {
        // Fallback to localStorage
        const journals = this.getLocalStorageJournals();
        const filteredJournals = journals.filter(journal => journal.id !== journalId);
        localStorage.setItem(this.localStorageKey, JSON.stringify(filteredJournals));
        console.log('🗑️ Journal deleted from localStorage:', journalId);
        return true;
      }
    } catch (error) {
      console.error('Error deleting journal:', error);
      
      // Try localStorage fallback
      try {
        const journals = this.getLocalStorageJournals();
        const filteredJournals = journals.filter(journal => journal.id !== journalId);
        localStorage.setItem(this.localStorageKey, JSON.stringify(filteredJournals));
        return true;
      } catch (fallbackError) {
        console.error('Fallback delete also failed:', fallbackError);
        return false;
      }
    }
  }

  // ================================
  // LOCAL STORAGE OPERATIONS
  // ================================

  /**
   * Fallback method to get journals from localStorage
   */
  getLocalStorageJournals() {
    try {
      const journals = localStorage.getItem(this.localStorageKey);
      const parsedJournals = journals ? JSON.parse(journals) : [];
      
      // Ensure it's always an array
      const journalsArray = Array.isArray(parsedJournals) ? parsedJournals : [];
      
      console.log(`📚 Loaded ${journalsArray.length} journals from localStorage (fallback)`);
      return journalsArray;
    } catch (error) {
      console.error('Error loading journals from localStorage:', error);
      return []; // Always return empty array on error
    }
  }

  /**
   * Save journal to localStorage with validation
   */
async saveJournalToLocalStorage(journalData) {
  try {
    const journals = this.getLocalStorageJournals();
    const journalId = Date.now().toString();
    const currentTimestamp = new Date().toISOString();
    
    const freshConversation = Array.isArray(journalData.conversation) 
      ? journalData.conversation.map(msg => ({
          ...msg,
          timestamp: msg.timestamp || currentTimestamp
        }))
      : [];
    
    const generatedTitle = journalData.title || this.generateTitle(journalData);
    
    const newJournal = {
      id: journalId,
      title: generatedTitle,
      header: generatedTitle, // ✅ Use title, not default
      summary: journalData.summary || '',
      insights: journalData.insights || '',
      conversation: freshConversation,
      sessionStartTime: journalData.sessionStartTime || currentTimestamp,
      sessionEndTime: currentTimestamp,
      messageCount: freshConversation.length,
      createdAt: currentTimestamp,
      tags: this.extractTags(journalData),
      imageUrl: this.generateUniqueImage(journalId),
      question: journalData.question || '',
      userId: journalData.userId,
      dataValidation: {
        conversationHash: this.generateConversationHash(freshConversation),
        savedAt: currentTimestamp,
        messageCount: freshConversation.length,
        source: 'localStorage_fallback'
      }
    };
    
    journals.unshift(newJournal);
    localStorage.setItem(this.localStorageKey, JSON.stringify(journals));
    console.log('💾 Journal saved to localStorage with correct title:', journalId);
    return newJournal;
  } catch (fallbackError) {
    console.error('❌ Fallback save also failed:', fallbackError);
    throw new Error('Failed to save journal');
  }
}

  /**
   * Clear localStorage journals after successful migration
   */
  clearLocalStorageJournals() {
    try {
      localStorage.removeItem(this.localStorageKey);
      console.log('🧹 Cleared localStorage journals after migration');
    } catch (error) {
      console.error('Error clearing localStorage journals:', error);
    }
  }

  // ================================
  // CONTENT GENERATION
  // ================================

  /**
   * Generate a title based on the journal content with validation
   */
  generateTitle(journalData) {
    const date = new Date().toLocaleDateString();
    const time = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    
    // Check if summary exists and is meaningful
    if (journalData.summary && journalData.summary.length > 10) {
      const words = journalData.summary.split(' ').slice(0, 6).join(' ');
      return `${words}... - ${date}`;
    }
    
    // Check conversation for content-based title
    if (journalData.conversation && Array.isArray(journalData.conversation)) {
      const userMessages = journalData.conversation.filter(msg => 
        msg.type === 'user' && msg.content && msg.content.length > 10
      );
      
      if (userMessages.length > 0) {
        const firstMessage = userMessages[0].content.split(' ').slice(0, 4).join(' ');
        return `${firstMessage}... - ${date}`;
      }
    }
    
    return `Journal Entry - ${date} ${time}`;
  }

  /**
   * Improved tag extraction with better precision and context awareness
   */
  extractTags(journalData) {
    const tags = [];
    const content = `${journalData.summary || ''} ${journalData.insights || ''} ${journalData.question || ''}`.toLowerCase();
    
    // More precise keyword matching with context and specificity
const tagPatterns = {
      // WORK & CAREER
      'work': {
        primary: ['workplace', 'office job', 'career', 'employment', 'professional', 'corporate', 'business meeting'],
        secondary: ['boss', 'manager', 'colleague', 'coworker', 'client', 'deadline', 'project', 'presentation', 'interview', 'promotion', 'salary', 'overtime', 'workload'],
        phrases: ['at work', 'work stress', 'work life', 'job interview', 'work pressure', 'work environment'],
        context: ['stressed about work', 'busy at work', 'work deadline', 'work meeting']
      },
      
      'career': {
        primary: ['career development', 'career path', 'career change', 'professional growth', 'career opportunity'],
        secondary: ['promotion', 'advancement', 'job change', 'career move', 'professional development', 'networking'],
        phrases: ['career goals', 'career planning', 'my career', 'career decision'],
        context: ['thinking about career', 'career crossroads', 'career progression', 'professional future']
      },
      
      'productivity': {
        primary: ['productive day', 'productivity hack', 'time management', 'getting things done', 'efficiency'],
        secondary: ['focused', 'accomplished', 'completed', 'organized', 'efficient', 'workflow', 'priorities'],
        phrases: ['was productive', 'got a lot done', 'managing time', 'staying focused'],
        context: ['productive morning', 'finished tasks', 'organized schedule', 'prioritized work']
      },
      
      'business': {
        primary: ['business meeting', 'business deal', 'business opportunity', 'entrepreneurship', 'startup'],
        secondary: ['entrepreneur', 'company', 'revenue', 'profit', 'client', 'contract', 'partnership'],
        phrases: ['business strategy', 'business plan', 'growing business', 'business decision'],
        context: ['business venture', 'starting a business', 'business success', 'business challenges']
      },
      
      // PERSONAL & FAMILY
      'family': {
        primary: ['family gathering', 'family dinner', 'family time', 'family reunion', 'family visit'],
        secondary: ['mom', 'mother', 'dad', 'father', 'sister', 'brother', 'parents', 'spouse', 'husband', 'wife', 'children', 'son', 'daughter', 'grandparents', 'aunt', 'uncle', 'cousin'],
        phrases: ['my mom', 'my dad', 'my family', 'with family', 'family matters', 'family issues'],
        context: ['talked to mom', 'visited family', 'family celebration', 'family support', 'family drama']
      },
      
      'parenting': {
        primary: ['parenting challenges', 'raising kids', 'being a parent', 'parenting moment', 'child development'],
        secondary: ['toddler', 'baby', 'teenager', 'school', 'daycare', 'discipline', 'bedtime', 'tantrums'],
        phrases: ['my kid', 'my child', 'as a parent', 'parenting is hard', 'kids activities'],
        context: ['dealing with kids', 'parenting stress', 'proud parent moment', 'school pickup']
      },
      
      'relationships': {
        primary: ['relationship issue', 'dating life', 'romantic relationship', 'friendship problem', 'connection'],
        secondary: ['boyfriend', 'girlfriend', 'partner', 'dating', 'breakup', 'romance', 'intimacy', 'communication'],
        phrases: ['my boyfriend', 'my girlfriend', 'relationship with', 'dating someone', 'in a relationship'],
        context: ['relationship stress', 'relationship goals', 'dating someone', 'broke up', 'getting serious']
      },
      
      'friendship': {
        primary: ['friendship issues', 'friend drama', 'close friend', 'best friend', 'friendship celebration'],
        secondary: ['friend', 'buddy', 'pal', 'companion', 'social circle', 'group chat', 'hanging out'],
        phrases: ['my friend', 'best friend', 'old friend', 'close friends', 'friend group'],
        context: ['met with friends', 'friend support', 'lost a friend', 'making friends', 'catching up']
      },
      
      'love': {
        primary: ['falling in love', 'love life', 'romantic feelings', 'being in love', 'love story'],
        secondary: ['crush', 'affection', 'romance', 'passion', 'attraction', 'soulmate', 'valentine'],
        phrases: ['in love', 'falling for', 'love someone', 'feeling love', 'romantic moment'],
        context: ['love is', 'loving relationship', 'express love', 'feeling loved', 'love and']
      },
      
      'dating': {
        primary: ['dating experience', 'first date', 'dating app', 'online dating', 'dating scene'],
        secondary: ['date', 'tinder', 'bumble', 'match', 'swipe', 'profile', 'relationship status'],
        phrases: ['going on date', 'dating someone', 'date night', 'dating world'],
        context: ['started dating', 'dating again', 'bad date', 'great date', 'dating life']
      },
      
      // HEALTH & WELLNESS
      'health': {
        primary: ['mental health', 'physical health', 'health check', 'doctor visit', 'medical appointment', 'health concern'],
        secondary: ['doctor', 'hospital', 'clinic', 'medicine', 'medication', 'treatment', 'diagnosis', 'symptoms', 'illness'],
        phrases: ['feeling sick', 'health issue', 'doctor said', 'medical problem', 'health concern'],
        context: ['went to doctor', 'feeling tired', 'health anxiety', 'doctor appointment', 'health scare']
      },
      
      'fitness': {
        primary: ['fitness journey', 'fitness goal', 'getting fit', 'workout routine', 'training session'],
        secondary: ['exercise', 'workout', 'gym', 'training', 'cardio', 'weights', 'strength', 'marathon'],
        phrases: ['went to gym', 'fitness routine', 'working out', 'exercise class', 'training for'],
        context: ['gym session', 'workout today', 'fitness progress', 'training hard', 'building muscle']
      },
      
      'mental-health': {
        primary: ['mental health', 'mental wellbeing', 'emotional health', 'psychological', 'mental state'],
        secondary: ['therapy', 'therapist', 'counseling', 'depression', 'anxiety', 'stress', 'burnout', 'self-care'],
        phrases: ['mental health day', 'seeing therapist', 'mental wellbeing', 'emotional state'],
        context: ['struggling mentally', 'mental health matters', 'therapy session', 'taking care of']
      },
      
      'nutrition': {
        primary: ['healthy eating', 'nutrition plan', 'diet change', 'meal prep', 'eating habits'],
        secondary: ['diet', 'meal', 'calories', 'protein', 'vegetables', 'healthy food', 'nutrition'],
        phrases: ['eating healthy', 'meal planning', 'nutrition goals', 'healthy diet'],
        context: ['changed diet', 'eating better', 'meal prepped', 'tracking calories', 'nutritious']
      },
      
      'wellness': {
        primary: ['wellness journey', 'holistic health', 'wellbeing', 'self-care routine', 'wellness practice'],
        secondary: ['balance', 'harmony', 'holistic', 'natural', 'healing', 'recovery', 'restoration'],
        phrases: ['wellness routine', 'self-care', 'taking care of myself', 'wellness goals'],
        context: ['wellness journey', 'holistic approach', 'balanced life', 'wellbeing matters']
      },
      
      'sleep': {
        primary: ['sleep issues', 'sleep quality', 'insomnia', 'sleep schedule', 'rest and recovery'],
        secondary: ['tired', 'exhausted', 'insomnia', 'nightmare', 'dream', 'rest', 'bed', 'nap'],
        phrases: ['cant sleep', 'slept well', 'sleep deprived', 'need rest', 'poor sleep'],
        context: ['sleeping better', 'trouble sleeping', 'good night sleep', 'sleep routine']
      },
      
      'exercise': {
        primary: ['exercise routine', 'physical activity', 'active lifestyle', 'movement practice'],
        secondary: ['running', 'jogging', 'swimming', 'cycling', 'walking', 'hiking', 'sports'],
        phrases: ['went running', 'exercise today', 'physical activity', 'active day'],
        context: ['exercised today', 'morning run', 'exercise habit', 'staying active']
      },
      
      // EMOTIONAL & MENTAL
      'emotions': {
        primary: ['feeling emotional', 'emotional day', 'mixed emotions', 'emotional stress', 'emotional roller coaster'],
        secondary: ['happy', 'sad', 'angry', 'frustrated', 'anxious', 'worried', 'excited', 'depressed', 'overwhelmed', 'joy', 'fear'],
        phrases: ['feeling happy', 'feeling sad', 'really angry', 'so excited', 'very worried'],
        context: ['emotional about', 'made me feel', 'feeling really', 'emotionally', 'emotions are']
      },
      
      'anxiety': {
        primary: ['anxiety attack', 'feeling anxious', 'anxiety disorder', 'anxious thoughts', 'panic attack'],
        secondary: ['worried', 'nervous', 'panic', 'fear', 'stress', 'overwhelmed', 'restless'],
        phrases: ['anxiety about', 'feeling anxious', 'panic attack', 'anxious thoughts'],
        context: ['anxiety is', 'dealing with anxiety', 'anxious about', 'panic mode']
      },
      
      'stress': {
        primary: ['stress management', 'stressed out', 'stressful day', 'under pressure', 'feeling stressed'],
        secondary: ['pressure', 'tension', 'overwhelm', 'burden', 'strain', 'overload'],
        phrases: ['so stressed', 'stress level', 'feeling pressure', 'stressed about'],
        context: ['stressed out', 'under stress', 'stressful situation', 'stress relief']
      },
      
      'happiness': {
        primary: ['feeling happy', 'happy moment', 'pure joy', 'happiness boost', 'joyful day'],
        secondary: ['joy', 'delight', 'cheerful', 'content', 'pleased', 'glad', 'bliss'],
        phrases: ['so happy', 'feeling joyful', 'made me happy', 'happy about'],
        context: ['happiness is', 'brought joy', 'happy moment', 'feeling blessed']
      },
      
      'gratitude': {
        primary: ['feeling grateful', 'gratitude practice', 'thankful for', 'appreciation', 'counting blessings'],
        secondary: ['grateful', 'thankful', 'blessed', 'appreciate', 'fortunate', 'lucky'],
        phrases: ['grateful for', 'thankful that', 'appreciate having', 'feeling blessed'],
        context: ['gratitude list', 'so grateful', 'thankful moment', 'appreciating life']
      },
      
      'mindfulness': {
        primary: ['mindfulness practice', 'being present', 'mindful moment', 'present awareness', 'conscious living'],
        secondary: ['present', 'aware', 'conscious', 'attentive', 'observing', 'noticing'],
        phrases: ['being mindful', 'present moment', 'mindful of', 'conscious awareness'],
        context: ['practicing mindfulness', 'mindful living', 'present in moment', 'aware of']
      },
      
      'meditation': {
        primary: ['meditation practice', 'meditation session', 'daily meditation', 'guided meditation'],
        secondary: ['meditate', 'breathing', 'breathwork', 'calm', 'peaceful', 'zen', 'quiet'],
        phrases: ['meditation today', 'meditated for', 'meditation practice', 'breathing exercise'],
        context: ['morning meditation', 'meditation helped', 'meditate daily', 'calm meditation']
      },
      
      // GROWTH & DEVELOPMENT
      'goals': {
        primary: ['life goals', 'personal goals', 'career goals', 'future plans', 'goal setting', 'ambitions'],
        secondary: ['goal', 'objective', 'target', 'aspiration', 'dream', 'plan', 'achievement', 'success', 'milestone'],
        phrases: ['my goal', 'want to achieve', 'planning to', 'hoping to', 'working towards'],
        context: ['set a goal', 'achieved my goal', 'goal is to', 'planning for', 'goal progress']
      },
      
      'learning': {
        primary: ['learning experience', 'educational', 'studying for', 'taking a course', 'skill development', 'knowledge'],
        secondary: ['school', 'college', 'university', 'study', 'class', 'course', 'lesson', 'exam', 'book', 'reading', 'tutorial'],
        phrases: ['learning about', 'studying for', 'reading a book', 'taking a class', 'new skill'],
        context: ['learned something', 'studying hard', 'exam tomorrow', 'course work', 'learning new']
      },
      
      'self-improvement': {
        primary: ['self-improvement', 'personal development', 'bettering myself', 'self-growth', 'improving life'],
        secondary: ['improve', 'better', 'develop', 'enhance', 'upgrade', 'transform', 'evolve'],
        phrases: ['improving myself', 'working on myself', 'becoming better', 'personal development'],
        context: ['self-improvement journey', 'improving habits', 'better version', 'growth mindset']
      },
      
      'education': {
        primary: ['education journey', 'academic', 'schoolwork', 'educational goal', 'degree program'],
        secondary: ['degree', 'diploma', 'certificate', 'graduation', 'semester', 'major', 'thesis'],
        phrases: ['my education', 'academic work', 'school project', 'college life'],
        context: ['educational path', 'studying at', 'academic goals', 'school semester']
      },
      
      'skills': {
        primary: ['skill development', 'learning skills', 'new ability', 'competency building', 'mastering'],
        secondary: ['skill', 'ability', 'talent', 'expertise', 'proficiency', 'competence', 'mastery'],
        phrases: ['developing skills', 'new skill', 'skill set', 'learning to'],
        context: ['skill building', 'mastering skill', 'improving skills', 'skill practice']
      },
      
      'personal-growth': {
        primary: ['personal growth', 'growing as person', 'self-discovery', 'transformation', 'evolution'],
        secondary: ['growth', 'develop', 'mature', 'evolve', 'change', 'transform', 'progress'],
        phrases: ['personal growth', 'growing personally', 'self-discovery', 'becoming who'],
        context: ['growth journey', 'personal evolution', 'discovering myself', 'growing stronger']
      },
      
      'reflection': {
        primary: ['self-reflection', 'reflecting on', 'introspection', 'contemplation', 'looking back'],
        secondary: ['reflect', 'think', 'contemplate', 'ponder', 'consider', 'examine'],
        phrases: ['reflecting on', 'thinking about', 'looking back', 'contemplating life'],
        context: ['reflection time', 'deep reflection', 'reflective mood', 'self-examination']
      },
      
      // CREATIVE & HOBBIES
      'creativity': {
        primary: ['creative project', 'artistic expression', 'creative process', 'creative work', 'creative flow'],
        secondary: ['create', 'express', 'imagine', 'invent', 'design', 'craft', 'make'],
        phrases: ['creative outlet', 'artistic project', 'making art', 'creative inspiration'],
        context: ['working on art', 'creative block', 'artistic vision', 'creative energy', 'being creative']
      },
      
      'hobbies': {
        primary: ['hobby time', 'favorite hobby', 'hobby project', 'leisure activity', 'pastime'],
        secondary: ['hobby', 'interest', 'passion', 'recreation', 'leisure', 'fun activity'],
        phrases: ['my hobby', 'hobby is', 'enjoying hobby', 'hobby project'],
        context: ['hobby time', 'working on hobby', 'new hobby', 'hobby interest']
      },
      
      'art': {
        primary: ['art project', 'creating art', 'artistic work', 'art piece', 'artistic expression'],
        secondary: ['painting', 'drawing', 'sketch', 'canvas', 'sculpture', 'gallery', 'exhibition'],
        phrases: ['making art', 'art work', 'artistic process', 'art project'],
        context: ['working on art', 'art inspiration', 'finished artwork', 'art studio']
      },
      
      'music': {
        primary: ['music practice', 'playing music', 'music performance', 'musical project', 'concert'],
        secondary: ['instrument', 'guitar', 'piano', 'singing', 'song', 'band', 'performance'],
        phrases: ['playing music', 'music practice', 'listening to', 'music lesson'],
        context: ['music session', 'played guitar', 'music practice', 'concert experience']
      },
      
      'writing': {
        primary: ['writing project', 'creative writing', 'writing process', 'journal writing', 'blog post'],
        secondary: ['write', 'story', 'novel', 'blog', 'article', 'essay', 'poetry', 'draft'],
        phrases: ['writing about', 'working on writing', 'writing project', 'wrote today'],
        context: ['writing session', 'creative writing', 'writing habit', 'finished writing']
      },
      
      'photography': {
        primary: ['photography project', 'photo shoot', 'taking photos', 'photography work', 'capture moment'],
        secondary: ['camera', 'photo', 'picture', 'shot', 'lens', 'shoot', 'capture'],
        phrases: ['took photos', 'photography session', 'photo project', 'captured moment'],
        context: ['photography today', 'photo shoot', 'taking pictures', 'photo editing']
      },
      
      // LIFESTYLE
      'travel': {
        primary: ['travel plans', 'vacation trip', 'business travel', 'travel experience', 'adventure travel'],
        secondary: ['trip', 'vacation', 'journey', 'flight', 'hotel', 'destination', 'explore', 'adventure', 'tourist'],
        phrases: ['going on trip', 'travel to', 'vacation in', 'exploring new', 'travel plans'],
        context: ['travel arrangements', 'vacation planning', 'trip was', 'traveling to', 'travel adventure']
      },
      
      'adventure': {
        primary: ['adventure time', 'outdoor adventure', 'adventure trip', 'adventurous experience'],
        secondary: ['explore', 'discovery', 'quest', 'expedition', 'outdoor', 'nature', 'wilderness'],
        phrases: ['adventure today', 'outdoor adventure', 'exploring nature', 'adventure time'],
        context: ['adventurous day', 'outdoor exploration', 'nature adventure', 'exploring outdoors']
      },
      
      'food': {
        primary: ['food experience', 'trying food', 'cooking food', 'restaurant visit', 'culinary'],
        secondary: ['cooking', 'recipe', 'meal', 'restaurant', 'dinner', 'lunch', 'breakfast', 'cuisine'],
        phrases: ['cooking dinner', 'tried new', 'delicious meal', 'food was'],
        context: ['cooking today', 'restaurant experience', 'food adventure', 'meal preparation']
      },
      
      'social': {
        primary: ['social event', 'social gathering', 'social life', 'hanging out', 'get together'],
        secondary: ['party', 'event', 'gathering', 'celebration', 'meeting people', 'socializing'],
        phrases: ['social event', 'hanging out', 'met people', 'social gathering'],
        context: ['social life', 'being social', 'social activity', 'party tonight']
      },
      
      'entertainment': {
        primary: ['entertainment tonight', 'fun activity', 'leisure time', 'entertainment event'],
        secondary: ['movie', 'show', 'concert', 'game', 'festival', 'theater', 'performance'],
        phrases: ['watched movie', 'went to show', 'entertainment tonight', 'fun time'],
        context: ['entertainment event', 'movie night', 'show was', 'concert experience']
      },
      
      // FINANCIAL
      'money': {
        primary: ['financial stress', 'money problems', 'financial planning', 'budget concerns', 'money matters'],
        secondary: ['money', 'cash', 'payment', 'expense', 'cost', 'price', 'afford', 'spend'],
        phrases: ['money issues', 'financial worry', 'cant afford', 'expensive purchase', 'saving money'],
        context: ['worried about money', 'financial situation', 'budget is tight', 'expensive', 'money stress']
      },
      
      'finance': {
        primary: ['financial planning', 'financial goals', 'financial health', 'financial decision'],
        secondary: ['financial', 'banking', 'account', 'credit', 'loan', 'mortgage', 'payment'],
        phrases: ['financial plan', 'financial goals', 'financial situation', 'financial advice'],
        context: ['financial planning', 'financial health', 'financial matters', 'financial security']
      },
      
      'investing': {
        primary: ['investment strategy', 'investing money', 'investment portfolio', 'stock market'],
        secondary: ['stocks', 'shares', 'portfolio', 'market', 'returns', 'dividends', 'crypto'],
        phrases: ['investing in', 'stock market', 'investment portfolio', 'market trends'],
        context: ['investment strategy', 'investing money', 'portfolio growth', 'market research']
      },
      
      'savings': {
        primary: ['savings goal', 'saving money', 'building savings', 'emergency fund', 'savings plan'],
        secondary: ['save', 'saved', 'savings', 'fund', 'nest egg', 'stash', 'reserve'],
        phrases: ['saving for', 'savings goal', 'saved money', 'building savings'],
        context: ['savings account', 'emergency fund', 'saving up', 'savings plan']
      },
      
      'budget': {
        primary: ['budget planning', 'monthly budget', 'staying on budget', 'budget review', 'budgeting'],
        secondary: ['budget', 'spending', 'expenses', 'bills', 'costs', 'financial plan'],
        phrases: ['on budget', 'budget constraints', 'monthly budget', 'tracking spending'],
        context: ['budget planning', 'tight budget', 'budget goals', 'staying within budget']
      },
      
      // SPIRITUAL & PHILOSOPHY
      'spirituality': {
        primary: ['spiritual journey', 'spiritual practice', 'spiritual growth', 'spiritual awakening'],
        secondary: ['spirit', 'soul', 'divine', 'sacred', 'prayer', 'worship', 'faith'],
        phrases: ['spiritual journey', 'spiritual practice', 'soul searching', 'spiritual growth'],
        context: ['spiritual connection', 'spiritual path', 'spiritual experience', 'soul level']
      },
      
      'philosophy': {
        primary: ['philosophical thinking', 'philosophical question', 'life philosophy', 'existential'],
        secondary: ['philosophy', 'meaning', 'purpose', 'existence', 'reality', 'truth'],
        phrases: ['philosophical thoughts', 'meaning of life', 'existential question', 'philosophy of'],
        context: ['philosophical discussion', 'deep thoughts', 'philosophical perspective', 'existential']
      },
      
      'faith': {
        primary: ['faith journey', 'religious practice', 'spiritual belief', 'faith community'],
        secondary: ['belief', 'religion', 'church', 'temple', 'mosque', 'worship', 'pray'],
        phrases: ['my faith', 'religious practice', 'faith community', 'spiritual belief'],
        context: ['faith journey', 'religious service', 'faith matters', 'belief system']
      },
      
      'purpose': {
        primary: ['life purpose', 'finding purpose', 'sense of purpose', 'purposeful living'],
        secondary: ['purpose', 'meaning', 'calling', 'mission', 'destiny', 'path'],
        phrases: ['life purpose', 'finding meaning', 'sense of purpose', 'purposeful life'],
        context: ['purpose journey', 'discovering purpose', 'meaningful life', 'life calling']
      },
      
      // DAILY LIFE
      'routine': {
        primary: ['daily routine', 'morning routine', 'evening routine', 'routine life', 'regular schedule'],
        secondary: ['routine', 'schedule', 'regular', 'daily', 'habit', 'pattern', 'ritual'],
        phrases: ['daily routine', 'morning routine', 'regular schedule', 'routine day'],
        context: ['routine activities', 'daily schedule', 'routine life', 'normal routine']
      },
      
      'habits': {
        primary: ['building habits', 'good habits', 'habit formation', 'breaking habits', 'habit change'],
        secondary: ['habit', 'practice', 'behavior', 'pattern', 'custom', 'tendency'],
        phrases: ['new habit', 'good habit', 'bad habit', 'habit formation'],
        context: ['building habits', 'habit tracker', 'habit change', 'daily habits']
      },
      
      'organization': {
        primary: ['getting organized', 'organization project', 'organizing space', 'decluttering'],
        secondary: ['organize', 'clean', 'tidy', 'declutter', 'arrange', 'sort', 'order'],
        phrases: ['getting organized', 'organizing today', 'cleaning up', 'decluttering space'],
        context: ['organization project', 'organized space', 'cleaning house', 'declutter life']
      },
      
      'home': {
        primary: ['home improvement', 'home life', 'home project', 'home comfort', 'domestic'],
        secondary: ['house', 'apartment', 'room', 'furniture', 'decor', 'renovation', 'maintenance'],
        phrases: ['at home', 'home project', 'home improvement', 'home life'],
        context: ['home renovation', 'home comfort', 'working from home', 'home sweet home']
      },
      
      'chores': {
        primary: ['household chores', 'doing chores', 'chore day', 'cleaning tasks'],
        secondary: ['cleaning', 'laundry', 'dishes', 'vacuuming', 'tidying', 'housework'],
        phrases: ['doing chores', 'household tasks', 'cleaning day', 'chore list'],
        context: ['finished chores', 'chore day', 'household duties', 'cleaning routine']
      },
      
      // CHALLENGES & PROBLEMS
      'challenges': {
        primary: ['facing challenges', 'challenge accepted', 'difficult challenge', 'overcoming challenges'],
        secondary: ['challenge', 'difficult', 'obstacle', 'hurdle', 'barrier', 'struggle'],
        phrases: ['facing challenge', 'challenge is', 'overcome challenge', 'dealing with'],
        context: ['challenge today', 'difficult challenge', 'facing obstacles', 'overcoming']
      },
      
      'problems': {
        primary: ['problem solving', 'dealing with problems', 'problem at hand', 'problem situation'],
        secondary: ['problem', 'issue', 'trouble', 'difficulty', 'complication', 'concern'],
        phrases: ['problem with', 'dealing with', 'solve problem', 'problem is'],
        context: ['problem today', 'facing problems', 'problem solving', 'issue arose']
      },
      
      'conflict': {
        primary: ['conflict resolution', 'conflict situation', 'interpersonal conflict', 'conflict at work'],
        secondary: ['conflict', 'disagreement', 'argument', 'dispute', 'clash', 'tension'],
        phrases: ['conflict with', 'argument about', 'disagreement over', 'tension between'],
        context: ['conflict resolution', 'dealing with conflict', 'argument today', 'tension at']
      },
      
      'difficulty': {
        primary: ['difficult time', 'going through difficulty', 'difficult situation', 'struggling with'],
        secondary: ['hard', 'tough', 'difficult', 'challenging', 'struggling', 'hardship'],
        phrases: ['difficult time', 'going through', 'struggling with', 'hard day'],
        context: ['difficult situation', 'tough times', 'struggling today', 'hardship faced']
      },
      
      // ACHIEVEMENTS & SUCCESS
      'achievement': {
        primary: ['proud achievement', 'accomplishment today', 'achieved goal', 'personal achievement'],
        secondary: ['accomplish', 'achieve', 'succeed', 'complete', 'finish', 'win'],
        phrases: ['achieved today', 'accomplished goal', 'proud of', 'achievement unlocked'],
        context: ['proud achievement', 'accomplished today', 'achievement moment', 'success story']
      },
      
      'success': {
        primary: ['success story', 'successful day', 'achieving success', 'success moment'],
        secondary: ['successful', 'triumph', 'victory', 'win', 'accomplish', 'breakthrough'],
        phrases: ['successful day', 'success at', 'achieved success', 'success story'],
        context: ['success today', 'successful outcome', 'success feels', 'victory moment']
      },
      
      'milestone': {
        primary: ['milestone reached', 'important milestone', 'milestone moment', 'major milestone'],
        secondary: ['milestone', 'landmark', 'turning point', 'breakthrough', 'benchmark'],
        phrases: ['reached milestone', 'milestone today', 'important milestone', 'milestone achieved'],
        context: ['milestone moment', 'reached milestone', 'major milestone', 'milestone celebration']
      },
      
      'victory': {
        primary: ['victory moment', 'sweet victory', 'victory celebration', 'winning moment'],
        secondary: ['victory', 'win', 'triumph', 'conquest', 'success', 'champion'],
        phrases: ['victory today', 'winning moment', 'triumphant day', 'victory lap'],
        context: ['sweet victory', 'victory celebration', 'won today', 'triumph over']
      },
      
      // OTHER
      'random': {
        primary: ['random thoughts', 'random day', 'random encounter', 'random experience'],
        secondary: ['random', 'spontaneous', 'unexpected', 'surprise', 'unplanned'],
        phrases: ['random thought', 'random moment', 'randomly', 'random experience'],
        context: ['random day', 'random encounter', 'random thoughts', 'spontaneous moment']
      },
      
      'thoughts': {
        primary: ['deep thoughts', 'random thoughts', 'thinking about', 'thought process'],
        secondary: ['think', 'thought', 'ponder', 'muse', 'consider', 'contemplate', 'wonder'],
        phrases: ['thinking about', 'thought about', 'my thoughts', 'random thoughts'],
        context: ['deep thoughts', 'thought process', 'thinking deeply', 'thoughts on']
      },
      
      'ideas': {
        primary: ['new idea', 'idea generation', 'brainstorming', 'creative idea', 'big idea'],
        secondary: ['idea', 'concept', 'notion', 'inspiration', 'brainwave', 'vision'],
        phrases: ['new idea', 'had idea', 'idea is', 'creative idea'],
        context: ['idea today', 'brainstorming ideas', 'idea generation', 'brilliant idea']
      },
      
      'dreams': {
        primary: ['had a dream', 'dream last night', 'recurring dream', 'dream about', 'weird dream'],
        secondary: ['dream', 'nightmare', 'vision', 'aspiration', 'sleeping', 'subconscious'],
        phrases: ['had dream', 'dream about', 'last night dream', 'weird dream'],
        context: ['dream last night', 'recurring dream', 'dream interpretation', 'vivid dream']
      },
      
      'memories': {
        primary: ['childhood memory', 'old memory', 'remembering', 'nostalgic memory', 'memory lane'],
        secondary: ['memory', 'remember', 'recall', 'reminisce', 'nostalgia', 'past'],
        phrases: ['remember when', 'old memory', 'memory of', 'reminiscing about'],
        context: ['memories of', 'memory lane', 'nostalgic moment', 'remembering past']
      },
      
      'plans': {
        primary: ['future plans', 'making plans', 'planning ahead', 'plan for', 'life plans'],
        secondary: ['plan', 'schedule', 'organize', 'arrange', 'prepare', 'strategy'],
        phrases: ['planning to', 'plans for', 'future plans', 'making plans'],
        context: ['plan ahead', 'planning future', 'plans today', 'strategic planning']
      },
      
      'decisions': {
        primary: ['making decision', 'big decision', 'decision time', 'tough decision', 'life decision'],
        secondary: ['decide', 'choice', 'option', 'determine', 'resolve', 'choose'],
        phrases: ['making decision', 'decided to', 'big decision', 'tough choice'],
        context: ['decision making', 'important decision', 'decided today', 'choice between']
      }
    }; 

    // Score-based classification with minimum thresholds
    Object.entries(tagPatterns).forEach(([tag, patterns]) => {
      let score = 0;
      let matchedTerms = [];

      // Check primary keywords (high weight)
      patterns.primary.forEach(keyword => {
        if (content.includes(keyword)) {
          score += 10;
          matchedTerms.push(keyword);
        }
      });

      // Check phrases (medium-high weight)
      patterns.phrases.forEach(phrase => {
        if (content.includes(phrase)) {
          score += 7;
          matchedTerms.push(phrase);
        }
      });

      // Check context patterns (medium weight)
      patterns.context.forEach(context => {
        if (content.includes(context)) {
          score += 5;
          matchedTerms.push(context);
        }
      });

      // Check secondary keywords (lower weight, need multiple matches)
      let secondaryMatches = 0;
      patterns.secondary.forEach(keyword => {
        if (content.includes(keyword)) {
          secondaryMatches++;
          matchedTerms.push(keyword);
        }
      });
      
      // Only add points for secondary if we have multiple matches or strong context
      if (secondaryMatches >= 2) {
        score += secondaryMatches * 2;
      } else if (secondaryMatches === 1 && score > 0) {
        score += 1;
      }

      // Add tag if score meets threshold
      const threshold = tag === 'emotions' ? 3 : 5; // Lower threshold for emotions
      if (score >= threshold) {
        tags.push(tag);
        console.log(`Tag "${tag}" added with score: ${score}, matches: ${matchedTerms.join(', ')}`);
      }
    });

    // Return top scoring tags that meet threshold
    const tagScores = {};
    Object.entries(tagPatterns).forEach(([tag, patterns]) => {
      let score = 0;
      patterns.primary.forEach(keyword => {
        if (content.includes(keyword)) score += 10;
      });
      patterns.phrases.forEach(phrase => {
        if (content.includes(phrase)) score += 7;
      });
      patterns.context.forEach(context => {
        if (content.includes(context)) score += 5;
      });
      let secondaryMatches = patterns.secondary.filter(keyword => content.includes(keyword)).length;
      if (secondaryMatches >= 2) score += secondaryMatches * 2;
      else if (secondaryMatches === 1 && score > 0) score += 1;
      
      tagScores[tag] = score;
    });

    // Return top scoring tags that meet threshold, limit to 4
    const finalTags = Object.entries(tagScores)
      .filter(([tag, score]) => score >= (tag === 'emotions' ? 3 : 5))
      .sort(([,a], [,b]) => b - a)
      .slice(0, 4)
      .map(([tag]) => tag);

    console.log('Final tags selected:', finalTags);
    return finalTags;
  }

  // ================================
  // QUERY & SEARCH OPERATIONS
  // ================================

  /**
   * Get journals by tag
   */
  async getJournalsByTag(tag) {
    try {
      const journals = await this.getAllJournals();
      return journals.filter(journal => journal.tags && journal.tags.includes(tag));
    } catch (error) {
      console.error('Error getting journals by tag:', error);
      return [];
    }
  }

  /**
   * Search journals by content
   */
  async searchJournals(searchTerm) {
    try {
      const journals = await this.getAllJournals();
      const term = searchTerm.toLowerCase();
      
      return journals.filter(journal => 
        (journal.title && journal.title.toLowerCase().includes(term)) ||
        (journal.summary && journal.summary.toLowerCase().includes(term)) ||
        (journal.insights && journal.insights.toLowerCase().includes(term)) ||
        (journal.header && journal.header.toLowerCase().includes(term))
      );
    } catch (error) {
      console.error('Error searching journals:', error);
      return [];
    }
  }

  /**
   * Get journals filtered by date range
   */
  async getJournalsByDateRange(startDate, endDate) {
    try {
      const journals = await this.getAllJournals();
      const start = new Date(startDate);
      const end = new Date(endDate);
      
      return journals.filter(journal => {
        const journalDate = new Date(journal.createdAt);
        return journalDate >= start && journalDate <= end;
      });
    } catch (error) {
      console.error('Error getting journals by date range:', error);
      return [];
    }
  }

  /**
   * Get recent journals (last N days)
   */
  async getRecentJournals(days = 7) {
    try {
      const journals = await this.getAllJournals();
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - days);
      
      return journals.filter(journal => new Date(journal.createdAt) >= cutoffDate);
    } catch (error) {
      console.error('Error getting recent journals:', error);
      return [];
    }
  }

  // ================================
  // ANALYTICS & STATISTICS
  // ================================

  /**
   * Get journal statistics
   */
  async getStats() {
    const journals = await this.getAllJournals();
    const now = new Date();
    const thisWeek = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7);
    const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);

    return {
      total: journals.length,
      thisWeek: journals.filter(j => new Date(j.createdAt) >= thisWeek).length,
      thisMonth: journals.filter(j => new Date(j.createdAt) >= thisMonth).length,
      averageLength: journals.reduce((acc, j) => acc + (j.summary?.length || 0), 0) / journals.length || 0,
      allTags: [...new Set(journals.flatMap(j => j.tags || []))]
    };
  }

  /**
   * Get journals count by month for analytics
   */
  async getJournalsCountByMonth(year = null) {
    try {
      const journals = await this.getAllJournals();
      const targetYear = year || new Date().getFullYear();
      const monthlyCounts = {};

      // Initialize all months to 0
      for (let month = 0; month < 12; month++) {
        monthlyCounts[month] = 0;
      }

      journals.forEach(journal => {
        const journalDate = new Date(journal.createdAt);
        if (journalDate.getFullYear() === targetYear) {
          monthlyCounts[journalDate.getMonth()]++;
        }
      });

      return monthlyCounts;
    } catch (error) {
      console.error('Error getting journals count by month:', error);
      return {};
    }
  }

  /**
   * Get storage usage information
   */
  async getStorageInfo() {
    try {
      const journals = await this.getAllJournals();
      const totalJournals = journals.length;
      
      // Calculate storage usage
      let totalSize = 0;
      journals.forEach(journal => {
        const journalStr = JSON.stringify(journal);
        totalSize += new Blob([journalStr]).size;
      });

      // Get localStorage usage
      let localStorageSize = 0;
      try {
        const localStorageData = localStorage.getItem(this.localStorageKey);
        if (localStorageData) {
          localStorageSize = new Blob([localStorageData]).size;
        }
      } catch (e) {
        // localStorage not available or error
      }

      return {
        totalJournals,
        totalSizeBytes: totalSize,
        totalSizeMB: (totalSize / 1024 / 1024).toFixed(2),
        localStorageSizeBytes: localStorageSize,
        localStorageSizeMB: (localStorageSize / 1024 / 1024).toFixed(2),
        averageJournalSizeBytes: totalJournals > 0 ? Math.round(totalSize / totalJournals) : 0,
        storageSource: this.isReady() ? 'Firestore' : 'localStorage',
        isReady: this.isReady()
      };
    } catch (error) {
      console.error('Error getting storage info:', error);
      return {
        totalJournals: 0,
        totalSizeBytes: 0,
        totalSizeMB: '0.00',
        localStorageSizeBytes: 0,
        localStorageSizeMB: '0.00',
        averageJournalSizeBytes: 0,
        storageSource: 'unknown',
        isReady: false,
        error: error.message
      };
    }
  }

  // ================================
  // DATA VALIDATION & UTILITIES
  // ================================

  /**
   * Validate journal data before saving
   */
  validateJournalData(journalData) {
    const errors = [];
    
    if (!journalData) {
      errors.push('Journal data is required');
      return { isValid: false, errors };
    }

    // Validate conversation
    if (!journalData.conversation || !Array.isArray(journalData.conversation)) {
      errors.push('Valid conversation array is required');
    }

    // Validate required fields exist (can be empty strings)
    const requiredFields = ['title', 'summary'];
    requiredFields.forEach(field => {
      if (journalData[field] === undefined || journalData[field] === null) {
        errors.push(`${field} is required`);
      }
    });

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  // ================================
  // IMPORT/EXPORT OPERATIONS
  // ================================

  /**
   * Export all journals
   */
  async exportAllJournals() {
    try {
      const journals = await this.getAllJournals();
      const exportData = {
        exportDate: new Date().toISOString(),
        journalCount: journals.length,
        userId: this.firestoreService?.getPreptuneUserId() || 'anonymous',
        source: this.isReady() ? 'firestore' : 'localStorage',
        journals: journals
      };
      
      const dataStr = JSON.stringify(exportData, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      
      const link = document.createElement('a');
      link.href = URL.createObjectURL(dataBlob);
      link.download = `voice-journals-export-${new Date().toISOString().split('T')[0]}.json`;
      link.click();
      
      return exportData;
    } catch (error) {
      console.error('Error exporting journals:', error);
      throw error;
    }
  }

  /**
   * Import journals from exported data
   */
  async importJournals(importData) {
    try {
      if (!importData.journals || !Array.isArray(importData.journals)) {
        throw new Error('Invalid import data format');
      }

      let importedCount = 0;
      const errors = [];

      for (const journalData of importData.journals) {
        try {
          // Generate new ID to avoid conflicts
          const newJournal = {
            ...journalData,
            importedAt: new Date().toISOString(),
            originalId: journalData.id
          };
          delete newJournal.id; // Remove original ID

          await this.saveJournal(newJournal);
          importedCount++;
        } catch (error) {
          console.error(`Failed to import journal ${journalData.id}:`, error);
          errors.push({ journalId: journalData.id, error: error.message });
        }
      }

      console.log(`✅ Imported ${importedCount} journals out of ${importData.journals.length}`);
      return { imported: importedCount, total: importData.journals.length, errors };
    } catch (error) {
      console.error('Error importing journals:', error);
      throw error;
    }
  }

  // ================================
  // BACKUP & RESTORE OPERATIONS
  // ================================

  /**
   * Backup journals to different storage location
   */
  async backupJournals(backupLocation = 'localStorage_backup') {
    try {
      const journals = await this.getAllJournals();
      const backupData = {
        backupDate: new Date().toISOString(),
        journalCount: journals.length,
        userId: this.firestoreService?.getPreptuneUserId() || 'anonymous',
        source: this.isReady() ? 'firestore' : 'localStorage',
        journals: journals
      };

      // Save to localStorage with backup key
      localStorage.setItem(backupLocation, JSON.stringify(backupData));
      console.log(`✅ Backed up ${journals.length} journals to ${backupLocation}`);
      
      return backupData;
    } catch (error) {
      console.error('Error backing up journals:', error);
      throw error;
    }
  }

  /**
   * Restore journals from backup
   */
  async restoreFromBackup(backupLocation = 'localStorage_backup') {
    try {
      const backupData = localStorage.getItem(backupLocation);
      if (!backupData) {
        throw new Error('No backup data found');
      }

      const parsedBackup = JSON.parse(backupData);
      if (!parsedBackup.journals || !Array.isArray(parsedBackup.journals)) {
        throw new Error('Invalid backup data format');
      }

      console.log(`🔄 Restoring ${parsedBackup.journals.length} journals from backup...`);
      
      let restoredCount = 0;
      const errors = [];

      for (const journal of parsedBackup.journals) {
        try {
          // Add restoration metadata
          const restoredJournal = {
            ...journal,
            restoredFrom: backupLocation,
            restoredAt: new Date().toISOString(),
            originalId: journal.id
          };
          delete restoredJournal.id; // Generate new ID

          await this.saveJournal(restoredJournal);
          restoredCount++;
        } catch (error) {
          console.error(`Failed to restore journal ${journal.id}:`, error);
          errors.push({ journalId: journal.id, error: error.message });
        }
      }

      console.log(`✅ Restored ${restoredCount} journals from backup`);
      return { restored: restoredCount, total: parsedBackup.journals.length, errors };
    } catch (error) {
      console.error('Error restoring from backup:', error);
      throw error;
    }
  }

  // ================================
  // MIGRATION OPERATIONS
  // ================================

  /**
   * Migrate localStorage journals to Firestore
   */
  async migrateLocalStorageJournals() {
    try {
      const localJournals = this.getLocalStorageJournals();
      if (localJournals.length === 0) {
        console.log('No localStorage journals to migrate');
        return { migrated: 0, errors: [] };
      }

      console.log(`🔄 Migrating ${localJournals.length} journals from localStorage to Firestore...`);

      let migratedCount = 0;
      const errors = [];

      for (const journal of localJournals) {
        try {
          if (this.isReady()) {
            const journalRef = doc(this.getJournalsCollectionRef(), journal.id);
            
            // Convert to Firestore format
            const firestoreJournal = {
              ...journal,
              userId: this.firestoreService?.getPreptuneUserId() || 'migrated-user',
              migratedFrom: 'localStorage',
              migratedAt: serverTimestamp(),
              createdAt: journal.createdAt ? new Date(journal.createdAt) : serverTimestamp(),
              sessionStartTime: journal.sessionStartTime ? new Date(journal.sessionStartTime) : serverTimestamp(),
              sessionEndTime: journal.sessionEndTime ? new Date(journal.sessionEndTime) : serverTimestamp()
            };

            await setDoc(journalRef, firestoreJournal);
            migratedCount++;
          }
        } catch (error) {
          console.error(`Failed to migrate journal ${journal.id}:`, error);
          errors.push({ journalId: journal.id, error: error.message });
        }
      }

      console.log(`✅ Migrated ${migratedCount} journals from localStorage to Firestore`);
      return { migrated: migratedCount, errors };
    } catch (error) {
      console.error('Error migrating journals:', error);
      return { migrated: 0, errors: [{ error: error.message }] };
    }
  }

/**
 * Upload journal images (stores base64 directly in Firestore)
 */
async uploadJournalImages(images, userId, journalId) {
  try {
    console.log(`📤 Processing ${images.length} images for journal ${journalId}`);
    
    // Return base64 data as-is (stored directly in Firestore)
    const processedImages = images.map((img, index) => ({
      data: img.data,
      id: img.id || `img_${Date.now()}_${index}`,
      width: img.width || 1920,
      height: img.height || 1080,
      aspectRatio: img.aspectRatio || 16/9
    }));
    
    console.log('✅ Images processed for storage');
    return processedImages.map(img => img.data);
    
  } catch (error) {
    console.error('❌ Failed to process images:', error);
    throw error;
  }
}
/**
 * Get a specific journal by ID (alias for getJournalById for compatibility)
 */
async getJournal(journalId) {
  return await this.getJournalById(journalId);
}

  /**
   * Fix existing journals that might not have images or updated tags
   */
  async fixExistingJournals() {
    try {
      const journals = await this.getAllJournals();
      let needsUpdate = false;
      const updates = [];

      for (const journal of journals) {
        let journalNeedsUpdate = false;
        const updatedFields = {};
        
        // Add image if missing or update if it's still using old URL format
        if (!journal.imageUrl || journal.imageUrl.includes('picsum.photos')) {
          updatedFields.imageUrl = this.generateUniqueImage(journal.id);
          journalNeedsUpdate = true;
          console.log(`Updated image for journal ${journal.id}:`, updatedFields.imageUrl);
        }
        
        // Add header if missing
        if (!journal.header) {
          updatedFields.header = 'Today\'s Reflection';
          journalNeedsUpdate = true;
        }
        
        // Re-extract tags with new improved system
        const newTags = this.extractTags(journal);
        if (JSON.stringify(journal.tags) !== JSON.stringify(newTags)) {
          updatedFields.tags = newTags;
          journalNeedsUpdate = true;
          console.log(`Updated tags for journal ${journal.id}:`, newTags);
        }
        
        if (journalNeedsUpdate) {
          updates.push({ id: journal.id, updates: updatedFields });
          needsUpdate = true;
        }
      }
      
      // Apply updates
      if (needsUpdate && this.isReady()) {
        for (const { id, updates: journalUpdates } of updates) {
          await this.updateJournal(id, journalUpdates);
        }
        console.log(`✅ Fixed ${updates.length} existing journals with improved classification`);
      } else if (needsUpdate) {
        // Fallback to localStorage updates
        const localJournals = this.getLocalStorageJournals();
        const updatedJournals = localJournals.map(journal => {
          const update = updates.find(u => u.id === journal.id);
          return update ? { ...journal, ...update.updates } : journal;
        });
        localStorage.setItem(this.localStorageKey, JSON.stringify(updatedJournals));
        console.log('✅ Fixed existing journals in localStorage');
      }
      
      return await this.getAllJournals();
    } catch (error) {
      console.error('Error fixing journals:', error);
      return await this.getAllJournals();
    }
  }

  // ================================
  // CLEANUP OPERATIONS
  // ================================

  /**
   * Clean up old or invalid journals
   */
  async cleanupJournals(options = {}) {
    const {
      removeEmptyJournals = false,
      removeOlderThanDays = null,
      removeDuplicates = false,
      dryRun = false
    } = options;

    try {
      const journals = await this.getAllJournals();
      const toRemove = [];
      const toUpdate = [];

      journals.forEach((journal, index) => {
        let shouldRemove = false;
        let needsUpdate = false;
        const updates = {};

        // Check for empty journals
        if (removeEmptyJournals) {
          const isEmpty = !journal.summary || 
                         (journal.summary.trim().length === 0 && 
                          (!journal.conversation || journal.conversation.length <= 1));
          if (isEmpty) {
            shouldRemove = true;
          }
        }

        // Check age
        if (removeOlderThanDays && !shouldRemove) {
          const journalAge = (Date.now() - new Date(journal.createdAt)) / (1000 * 60 * 60 * 24);
          if (journalAge > removeOlderThanDays) {
            shouldRemove = true;
          }
        }

        // Fix missing fields
        if (!shouldRemove) {
          if (!journal.imageUrl) {
            updates.imageUrl = this.generateUniqueImage(journal.id);
            needsUpdate = true;
          }
          
          if (!journal.header) {
            updates.header = 'Today\'s Reflection';
            needsUpdate = true;
          }

          if (!journal.tags || !Array.isArray(journal.tags)) {
            updates.tags = this.extractTags(journal);
            needsUpdate = true;
          }
        }

        if (shouldRemove) {
          toRemove.push(journal.id);
        } else if (needsUpdate) {
          toUpdate.push({ id: journal.id, updates });
        }
      });

      // Handle duplicates
      if (removeDuplicates) {
        const seen = new Map();
        journals.forEach(journal => {
          const key = `${journal.title}_${journal.createdAt}`;
          if (seen.has(key)) {
            if (!toRemove.includes(journal.id)) {
              toRemove.push(journal.id);
            }
          } else {
            seen.set(key, journal.id);
          }
        });
      }

      const cleanupSummary = {
        totalJournals: journals.length,
        toRemove: toRemove.length,
        toUpdate: toUpdate.length,
        removedIds: toRemove,
        updatedIds: toUpdate.map(u => u.id),
        dryRun
      };

      if (!dryRun) {
        // Remove journals
        for (const id of toRemove) {
          await this.deleteJournal(id);
        }

        // Update journals
        for (const { id, updates } of toUpdate) {
          await this.updateJournal(id, updates);
        }

        console.log(`🧹 Cleanup completed: removed ${toRemove.length}, updated ${toUpdate.length}`);
      } else {
        console.log(`🔍 Cleanup dry run: would remove ${toRemove.length}, update ${toUpdate.length}`);
      }

      return cleanupSummary;
    } catch (error) {
      console.error('Error during cleanup:', error);
      throw error;
    }
  }
}

export default JournalStorageService;